"use client";

import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

export type MonthOption = { value: string; fullLabel: string; longLabel?: string };

export type PriceByMonthValue = {
    price: number;
    originalPrice: number;
    hasDiscount: boolean;
    discountPercentage: number | null;
};

export type SelectedMonthFallback = {
    price: number | null;
    originalPrice: number | null;
    hasDiscount: boolean;
    discountPercentage: number | null;
};

export interface TourDepartureMonthPickerProps {
    monthOptions: MonthOption[];
    priceByMonth: Map<string, PriceByMonthValue>;
    selectedMonth: string;
    selectedMonthFallback: SelectedMonthFallback | null;
    /** YYYY-MM — sadece bu aylarda çıkış vardır; fiyat yalnızca bunlarda gösterilir */
    monthsWithDepartures: ReadonlySet<string>;
    currencySymbol: string;
    language: string;
    year: number;
    onYearPrev: () => void;
    onYearNext: () => void;
    onMonthSelect: (monthValue: string) => void;
    isOpen: boolean;
    onTriggerClick: () => void;
    triggerLabel: string;
    ariaLabelPrevYear: string;
    ariaLabelNextYear: string;
    ariaLabelMonth: string;
    triggerClassName?: string;
    containerRef?: React.RefObject<HTMLDivElement | null>;
}

export default function TourDepartureMonthPicker({
    monthOptions,
    priceByMonth,
    selectedMonth,
    selectedMonthFallback,
    monthsWithDepartures,
    currencySymbol,
    language,
    year,
    onYearPrev,
    onYearNext,
    onMonthSelect,
    isOpen,
    onTriggerClick,
    triggerLabel,
    ariaLabelPrevYear,
    ariaLabelNextYear,
    ariaLabelMonth,
    triggerClassName = "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-alsero-dark-gray flex items-center justify-between hover:border-gray-400",
    containerRef,
}: TourDepartureMonthPickerProps) {
    const locale = language === "tr" ? "tr-TR" : "en-GB";
    const formatPrice = (n: number) => `${currencySymbol}${Number(n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <div className="relative" ref={containerRef ?? undefined}>
            <button
                type="button"
                onClick={onTriggerClick}
                className={triggerClassName}
                aria-label={ariaLabelMonth}
                aria-expanded={isOpen}
            >
                <span className="inline-flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
                    {triggerLabel}
                </span>
                <svg
                    className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>
            {isOpen && (
                <div className="absolute z-20 mt-2 w-full min-w-[280px] rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
                    <div className="mb-3 flex items-center justify-between">
                        <button
                            type="button"
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-alsero-dark-gray"
                            onClick={onYearPrev}
                            aria-label={ariaLabelPrevYear}
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <span className="text-sm font-semibold text-alsero-dark-gray">{year}</span>
                        <button
                            type="button"
                            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-alsero-dark-gray"
                            onClick={onYearNext}
                            aria-label={ariaLabelNextYear}
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {monthOptions.map((month) => {
                            const isSelected = month.value === selectedMonth;
                            const isSelectedMonth = month.value === selectedMonth;
                            const hasDeparture = monthsWithDepartures.has(month.value);
                            const fromPriceByMonth = priceByMonth.get(month.value);
                            let monthPrice: number | null = null;
                            let monthOriginalPrice: number | null = null;
                            let monthHasDiscount = false;
                            let monthDiscountPct: number | null = null;
                            if (fromPriceByMonth) {
                                monthPrice = fromPriceByMonth.price;
                                monthHasDiscount = fromPriceByMonth.hasDiscount;
                                monthDiscountPct = fromPriceByMonth.discountPercentage ?? null;
                                if (monthHasDiscount) monthOriginalPrice = fromPriceByMonth.originalPrice;
                            } else if (isSelectedMonth && selectedMonthFallback) {
                                monthPrice = selectedMonthFallback.price;
                                monthOriginalPrice = selectedMonthFallback.originalPrice;
                                monthHasDiscount = selectedMonthFallback.hasDiscount;
                                monthDiscountPct = selectedMonthFallback.discountPercentage;
                            }
                            const priceStr = monthPrice != null ? formatPrice(monthPrice) : "";
                            const originalStr = monthOriginalPrice != null ? formatPrice(monthOriginalPrice) : "";
                            return (
                                <button
                                    key={month.value}
                                    type="button"
                                    disabled={!hasDeparture}
                                    onClick={() => onMonthSelect(month.value)}
                                    className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-sm font-medium transition-all ${
                                        !hasDeparture
                                            ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400"
                                            : isSelected
                                              ? "border-alsero-dark-gray bg-alsero-dark-gray text-white shadow-sm"
                                              : monthHasDiscount
                                                ? "border-amber-200 bg-amber-50/80 text-gray-800 hover:border-amber-300 hover:bg-amber-50"
                                                : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                    }`}
                                    title={month.fullLabel}
                                >
                                    <span className="flex items-center gap-1">
                                        {month.longLabel ?? month.fullLabel}
                                        {monthHasDiscount && monthDiscountPct != null && (
                                            <span
                                                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-tight ${isSelected ? "bg-white/25 text-amber-100" : "bg-amber-200/90 text-amber-900"}`}
                                            >
                                                -{monthDiscountPct}%
                                            </span>
                                        )}
                                    </span>
                                    {(priceStr || originalStr) && (
                                        <span
                                            className={`flex flex-col items-center text-xs ${isSelected ? "text-white" : ""} ${monthHasDiscount && !isSelected ? "text-gray-800" : ""}`}
                                        >
                                            {monthHasDiscount && originalStr && (
                                                <span className={isSelected ? "line-through opacity-80 text-white/90" : "line-through opacity-75"}>{originalStr}</span>
                                            )}
                                            {priceStr && (
                                                <span className={monthHasDiscount ? (isSelected ? "font-semibold text-white" : "font-semibold text-alsero-dark-gray") : ""}>
                                                    {priceStr}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
